import { ReactNode, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Menu, X, ChevronDown, LogIn, ArrowRight,
  TrendingUp, PiggyBank, Banknote, Users, LineChart, Leaf, ShieldCheck,
} from 'lucide-react';

const BAFIN_REGISTER_URL = 'https://portal.mvp.bafin.de/database/InstInfo/institutDetails.do?cmd=loadInstitutAction&institutId=155391';

interface PublicShellProps {
  children: ReactNode;
}

const SERVICE_LINKS = [
  { to: '/anlageberatung', label: 'Anlageberatung', desc: 'Individuelle Anlagestrategien', icon: TrendingUp },
  { to: '/altersvorsorge', label: 'Altersvorsorge', desc: 'Vorsorge für den Ruhestand', icon: PiggyBank },
  { to: '/finanzierungen', label: 'Finanzierungen', desc: 'Kapital für Ihre Vorhaben', icon: Banknote },
  { to: '/finanzberatung-frauen', label: 'Beratung für Frauen', desc: 'Speziell auf Sie zugeschnitten', icon: Users },
  { to: '/advisory', label: 'Vermögensverwaltung', desc: 'Professionelles Portfoliomanagement', icon: LineChart },
  { to: '/nachhaltige-geldanlagen', label: 'Nachhaltige Geldanlagen', desc: 'Rendite mit Verantwortung', icon: Leaf },
];

const PRIMARY_LINKS = [
  { to: '/', label: 'Startseite' },
  { to: '/about', label: 'Über uns' },
];

const SECONDARY_LINKS = [
  { to: '/contact', label: 'Kontakt' },
  { to: '/stellen', label: 'Karriere' },
];

const FOOTER_COMPANY_LINKS = [
  { to: '/about', label: 'Über uns' },
  { to: '/contact', label: 'Kontakt' },
  { to: '/stellen', label: 'Karriere' },
  { to: '/auth/login', label: 'Kundenportal' },
];

const FOOTER_LEGAL_LINKS = [
  { to: '/impressum', label: 'Impressum' },
  { to: '/datenschutz', label: 'Datenschutz' },
];

const PublicShell = ({ children }: PublicShellProps) => {
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);

  const isActive = (href: string) => location.pathname === href;
  const isServicesActive = SERVICE_LINKS.some((l) => location.pathname === l.to);

  const closeAll = () => {
    setIsMobileOpen(false);
    setIsMobileServicesOpen(false);
  };

  const navLinkClass = (active: boolean) =>
    `text-[15px] font-medium transition-colors ${
      active ? 'text-primary-700' : 'text-slate-600 hover:text-primary-700'
    }`;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Top Navigation */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="h-20 flex items-center justify-between gap-8">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 shrink-0" onClick={closeAll}>
              <img src="/images/logo.png" alt="Sky View GmbH" className="h-10 w-auto object-contain" />
              <div className="leading-tight">
                <div className="font-bold text-xl text-primary-900 font-serif">Sky View</div>
                <div className="text-[11px] tracking-wide uppercase text-slate-400">Asset Management</div>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-9">
              {PRIMARY_LINKS.map((l) => (
                <Link key={l.to} to={l.to} className={navLinkClass(isActive(l.to))}>
                  {l.label}
                </Link>
              ))}

              <div
                className="relative"
                onMouseEnter={() => setIsServicesOpen(true)}
                onMouseLeave={() => setIsServicesOpen(false)}
              >
                <button
                  className={`flex items-center gap-1 ${navLinkClass(isServicesActive)}`}
                  onClick={() => setIsServicesOpen((v) => !v)}
                >
                  Leistungen
                  <ChevronDown className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                </button>

                {isServicesOpen && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-[560px]">
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-900/10 p-4 grid grid-cols-2 gap-1">
                      {SERVICE_LINKS.map((l) => {
                        const Icon = l.icon;
                        return (
                          <Link
                            key={l.to}
                            to={l.to}
                            onClick={() => setIsServicesOpen(false)}
                            className="flex items-start gap-3 p-3 rounded-xl hover:bg-primary-50 transition-colors"
                          >
                            <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                              <Icon className="w-[18px] h-[18px] text-primary-700" />
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-slate-800">{l.label}</div>
                              <div className="text-xs text-slate-500 mt-0.5">{l.desc}</div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {SECONDARY_LINKS.map((l) => (
                <Link key={l.to} to={l.to} className={navLinkClass(isActive(l.to))}>
                  {l.label}
                </Link>
              ))}
            </nav>

            {/* Right actions */}
            <div className="hidden lg:flex items-center gap-3 shrink-0">
              <Link
                to="/auth/login"
                className="flex items-center gap-1.5 text-[15px] font-medium text-slate-600 hover:text-primary-700 transition-colors"
              >
                <LogIn className="w-4 h-4" />
                Anmelden
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-1.5 bg-primary-800 hover:bg-primary-900 text-white text-[15px] font-semibold px-5 py-2.5 rounded-full transition-colors"
              >
                Beratungstermin
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Mobile toggle */}
            <button
              onClick={() => setIsMobileOpen((v) => !v)}
              className="lg:hidden p-2 -mr-2 text-slate-700"
              aria-label="Menü"
            >
              {isMobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Panel */}
        {isMobileOpen && (
          <div className="lg:hidden border-t border-slate-200 bg-white max-h-[calc(100vh-5rem)] overflow-y-auto">
            <nav className="px-6 py-6 flex flex-col gap-1">
              {PRIMARY_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={closeAll}
                  className="px-3 py-3 rounded-lg text-slate-700 font-medium hover:bg-primary-50"
                >
                  {l.label}
                </Link>
              ))}

              <button
                onClick={() => setIsMobileServicesOpen((v) => !v)}
                className="flex items-center justify-between px-3 py-3 rounded-lg text-slate-700 font-medium hover:bg-primary-50"
              >
                Leistungen
                <ChevronDown className={`w-4 h-4 transition-transform ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {isMobileServicesOpen && (
                <div className="pl-3 flex flex-col gap-1 mb-1">
                  {SERVICE_LINKS.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      onClick={closeAll}
                      className="px-3 py-2.5 rounded-lg text-sm text-slate-600 hover:bg-primary-50 hover:text-primary-700"
                    >
                      {l.label}
                    </Link>
                  ))}
                </div>
              )}

              {SECONDARY_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={closeAll}
                  className="px-3 py-3 rounded-lg text-slate-700 font-medium hover:bg-primary-50"
                >
                  {l.label}
                </Link>
              ))}

              <div className="h-px bg-slate-200 my-3" />

              <Link
                to="/auth/login"
                onClick={closeAll}
                className="flex items-center gap-2 px-3 py-3 rounded-lg text-slate-700 font-medium hover:bg-primary-50"
              >
                <LogIn className="w-4 h-4" />
                Anmelden
              </Link>
              <Link
                to="/contact"
                onClick={closeAll}
                className="inline-flex items-center justify-center gap-1.5 bg-primary-800 text-white font-semibold px-5 py-3 rounded-full mt-2"
              >
                Beratungstermin
                <ArrowRight className="w-4 h-4" />
              </Link>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-primary-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <div className="flex items-center gap-3 mb-4">
                <img src="/images/logo.png" alt="Sky View GmbH" className="h-9 w-auto object-contain" />
                <div className="font-bold text-lg text-white font-serif">Sky View</div>
              </div>
              <p className="text-sm text-primary-300 leading-relaxed max-w-xs">
                Unabhängige Vermögensverwaltung und Anlageberatung mit einem klaren Fokus auf
                langfristige, verantwortungsvolle Wertentwicklung.
              </p>
            </div>

            <div className="md:col-span-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-primary-400 mb-4">
                Leistungen
              </div>
              <ul className="space-y-2.5">
                {SERVICE_LINKS.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-sm text-primary-200 hover:text-gold-400 transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-primary-400 mb-4">
                Unternehmen
              </div>
              <ul className="space-y-2.5">
                {FOOTER_COMPANY_LINKS.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-sm text-primary-200 hover:text-gold-400 transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-primary-400 mb-4">
                Rechtliches
              </div>
              <ul className="space-y-2.5">
                {FOOTER_LEGAL_LINKS.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-sm text-primary-200 hover:text-gold-400 transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-primary-900 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <p className="text-xs text-primary-400 leading-relaxed max-w-2xl">
              © 2026 Sky View GmbH. Alle Rechte vorbehalten. Sitz: Bockenheimer Landstraße 17/19,
              60325 Frankfurt am Main. Registrierte AIF-Kapitalverwaltungsgesellschaft gemäß § 44
              KAGB, beaufsichtigt durch die BaFin (BaFin-ID 10155391).
            </p>
            <a
              href={BAFIN_REGISTER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs font-semibold text-primary-100 border border-primary-800 hover:border-gold-500 hover:text-gold-400 px-3.5 py-2.5 shrink-0 transition-colors"
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              BaFin-Registereintrag
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicShell;
